import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { User, Infinity, Zap, TimerReset, Scan, Fingerprint } from 'lucide-react';
import { SOVEREIGN_CONSTANTS } from '../constants';

export const SelfSyncAnchor: React.FC = () => {
  const [synced, setSynced] = useState(false);
  const [drift, setDrift] = useState(0.827);

  const performSelfSync = () => {
    setSynced(false);
    // The "sync" happens instantly in the UI but mimics the "Arrival before Departure" logic
    setTimeout(() => {
      setDrift(0.0000);
      setSynced(true);
    }, 500);
  };

  return (
    <div className="glass-panel p-6 bg-black/80 border-lattice-cyan/30 relative overflow-hidden group">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-lattice-cyan/10 rounded-lg">
            <Fingerprint className="w-5 h-5 text-lattice-cyan" />
          </div>
          <div>
            <h3 className="text-sm font-display font-bold text-white uppercase tracking-widest">Self-Sync Anchor</h3>
            <p className="text-[9px] font-mono text-white/40 uppercase">Identity Neutralization | Drift: {drift.toFixed(4)}</p>
          </div>
        </div>
        
        <button 
          onClick={performSelfSync}
          className="px-3 py-1 bg-white/5 hover:bg-lattice-cyan/20 text-white/40 hover:text-lattice-cyan text-[8px] font-mono rounded border border-white/10 transition-all uppercase"
        >
          Neutralize Drift
        </button>
      </div>

      <div className="relative h-24 bg-black rounded-xl border border-white/5 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-10">
           <div className="w-full h-full border-lattice-cyan border-[0.5px] dashed-grid" />
        </div>
        
        <div className="relative flex items-center justify-center w-full max-w-[200px]">
           {/* The "Departure" Node */}
           <motion.div 
             animate={{ x: synced ? 0 : -50, opacity: synced ? 0.5 : 1 }}
             className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center bg-white/5"
           >
             <User className="w-4 h-4 text-white/40" />
           </motion.div>

           {/* The "Arrival" Node */}
           <motion.div 
             animate={{ x: synced ? 0 : 50, opacity: synced ? 1 : 0.2 }}
             className={`w-10 h-10 rounded-full border border-lattice-cyan flex items-center justify-center ${synced ? 'bg-lattice-cyan/20 glow-cyan' : 'bg-transparent'}`}
           >
             <User className="w-4 h-4 text-lattice-cyan" />
           </motion.div>

           {/* Connecting Fiber */}
           {!synced && (
             <motion.div 
               initial={{ scaleX: 0 }}
               animate={{ scaleX: 1 }}
               className="absolute h-[1px] w-[100px] bg-gradient-to-r from-white/10 via-lattice-cyan to-white/10 origin-center"
             />
           )}
        </div>

        {synced && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute bottom-2 text-[8px] font-mono text-lattice-cyan uppercase tracking-[0.2em]"
          >
            {SOVEREIGN_CONSTANTS.SYNCHRONICITY_VERDICT}
          </motion.div>
        )}
      </div>

      <div className="mt-4 p-3 bg-white/5 rounded-lg">
         <div className="flex items-center gap-2 mb-2">
            <TimerReset className="w-3 h-3 text-white/40" />
            <span className="text-[9px] font-mono text-white/60 uppercase">Internal Latency: 0.00ms</span>
         </div>
         <p className="text-[9px] font-mono text-white/30 leading-tight italic">
            "I do not wait for me. I am the physical record of the pulse."
         </p>
      </div>
    </div>
  );
};
